import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export interface DataState {
  loading: boolean
  error: string | null
  data: Array<{
    id: string
    name: string
    type: string
    value: number
    growth: number
    date: string
    category: string
  }>
  filters: {
    dateRange: [string, string] | null
    type: string
    category: string
    minValue: number | null
    maxValue: number | null
  }
  pagination: {
    current: number
    pageSize: number
    total: number
  }
}

const initialState: DataState = {
  loading: false,
  error: null,
  data: [],
  filters: {
    dateRange: null,
    type: 'all',
    category: 'all',
    minValue: null,
    maxValue: null,
  },
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
}

// 异步thunk
export const fetchData = createAsyncThunk(
  'data/fetchData',
  async (_params: any) => {
    // 模拟API调用
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // 模拟数据
    const mockData = Array.from({ length: 50 }, (_, i) => ({
      id: `DATA${(i + 1).toString().padStart(3, '0')}`,
      name: `数据项 ${i + 1}`,
      type: ['股票', '债券', '房地产', '数字货币'][i % 4],
      value: Math.floor(Math.random() * 1000000) + 100000,
      growth: Number((Math.random() * 30 - 10).toFixed(1)),
      date: `2024-01-${(i % 30 + 1).toString().padStart(2, '0')}`,
      category: ['权益类', '固收类', '不动产', '另类投资'][i % 4],
    }))
    
    return {
      data: mockData,
      total: mockData.length,
    }
  }
)

const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<DataState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
      state.pagination.current = 1 // 重置页码
    },
    setPagination: (state, action: PayloadAction<Partial<DataState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
      state.pagination.current = 1
    },
    updateDataItem: (state, action: PayloadAction<{ id: string; updates: Partial<DataState['data'][0]> }>) => {
      const index = state.data.findIndex(item => item.id === action.payload.id)
      if (index !== -1) {
        state.data[index] = { ...state.data[index], ...action.payload.updates }
      }
    },
    deleteDataItem: (state, action: PayloadAction<string>) => {
      state.data = state.data.filter(item => item.id !== action.payload)
      state.pagination.total = state.data.length
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchData.fulfilled, (state, action) => {
        state.loading = false
        state.data = action.payload.data
        state.pagination.total = action.payload.total
      })
      .addCase(fetchData.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || '获取数据失败'
      })
  },
})

export const {
  setFilters,
  setPagination,
  clearFilters,
  updateDataItem,
  deleteDataItem,
  clearError,
} = dataSlice.actions

export default dataSlice.reducer